import type { CSSObject } from "type/tss";
import { Theme, alpha } from "@mui/material/styles";
import { baseColors } from "theme";

export const styles = (theme: Theme) => {
	return ({
		root: {
			width: "100%",
		},
		// The closed control: pills sit inside the field, chevron on the right. Field states match
		// FormSelectField, the design-system dropdown (MAX-4337).
		field: {
			display: "flex",
			alignItems: "center",
			gap: 8,
			minHeight: 56,
			padding: "12px 16px",
			border: `1px solid ${theme.palette.custom.border}`,
			borderRadius: 4,
			cursor: "pointer",
			backgroundColor: theme.palette.background.paper,
			"&:hover": {
				backgroundColor: baseColors.gray75,
				borderColor: alpha(theme.palette.secondary.main, 0.4),
			},
		},
		// A 2px focus border without shifting the pills: the inset shadow supplies the second pixel.
		fieldOpen: {
			backgroundColor: baseColors.gray75,
			borderColor: theme.palette.secondary.main,
			boxShadow: `inset 0 0 0 1px ${theme.palette.secondary.main}`,
			"&:hover": {
				borderColor: theme.palette.secondary.main,
			},
		},
		pills: {
			display: "flex",
			alignItems: "center",
			gap: 8,
			flex: 1,
			minWidth: 0,
			overflow: "hidden",
		},
		pill: {
			display: "inline-flex",
			alignItems: "center",
			gap: 8,
			height: 32,
			padding: "0 16px",
			borderRadius: 16,
			fontSize: 14,
			whiteSpace: "nowrap",
			backgroundColor: theme.palette.custom.tagNeutralSurface,
			color: theme.palette.custom.tagNeutralText,
		},
		pillRemove: {
			display: "inline-flex",
			alignItems: "center",
			cursor: "pointer",
			opacity: 0.7,
			"&:hover": { opacity: 1 },
		},
		// "+N more" when the pills do not fit.
		overflowPill: {
			flexShrink: 0,
		},
		placeholder: {
			color: theme.palette.custom.textSecondary,
			fontSize: 16,
		},
		chevron: {
			display: "inline-flex",
			flexShrink: 0,
			color: theme.palette.secondary.main,
			transition: "transform 150ms",
		},
		chevronOpen: {
			transform: "rotate(180deg)",
		},
		// Figma 1709:26749: #EBEBEB outline with a soft shadow, 48px option rows (12px top/bottom,
		// 16px left, 24px line) and a 24px checkbox 10px from its label (MAX-4337).
		menu: {
			maxHeight: 320,
			overflowY: "auto",
			border: `1px solid ${theme.palette.custom.border}`,
			borderRadius: 4,
			boxShadow: "0 4px 4px rgba(142, 141, 208, 0.16)",
		},
		option: {
			padding: "12px 16px",
			gap: 10,
			"& .MuiCheckbox-root": {
				padding: 0,
			},
		},
		optionText: {
			margin: 0,
			"& .MuiListItemText-primary": {
				lineHeight: "24px",
			},
		},
		optionLabel: {
			fontSize: 16,
			color: theme.palette.custom.textPrimary,
		},
		// The "+ Add recipients or lead forwarding email" row at the foot of the dropdown, in the
		// tertiary button style: Semibold 16/20, teal tertiary-action (MAX-4337).
		addRow: {
			display: "flex",
			alignItems: "center",
			gap: 4,
			height: 56,
			boxSizing: "border-box",
			padding: "0 16px",
			borderTop: `1px solid ${theme.palette.custom.divider}`,
			color: theme.palette.custom.tertiaryAction,
			fontSize: 16,
			fontWeight: 600,
			lineHeight: "20px",
			cursor: "pointer",
			"&:hover": {
				backgroundColor: theme.palette.action.hover,
			},
		},
	} satisfies Record<string, CSSObject>);
};
